if (!customElements.get('product-list')) {
  const PLP_DEBUG = /(?:^|[?&])plp-debug(?:=|&|$)/.test(window.location.search);
  const log = PLP_DEBUG ? console.log.bind(console) : () => {};
  const logTable = PLP_DEBUG ? console.table.bind(console) : () => {};

  const PLP_PREVIEW = /(?:^|[?&])preview=true(?:&|$)/.test(window.location.search);

  const ESSENTIAL_TILE_COUNT = 4;

  const LEAD_ASSET_STAGGER_MS = 80;

  const IMAGE_WIDTHS = [300, 450, 600, 750, 900, 1200];
  const MAX_VISIBLE_SWATCHES = 4;
  const FACET_VISIBLE_LIMIT = 10;
  const PLACEHOLDER_CLASS = 'sp-block sp-bg-[#f7f7f7]';

  const SORT_MAP = {
    'price-ascending': [{ field: 'price', order: 'asc' }],
    'price-descending': [{ field: 'price', order: 'desc' }],
  };

  const ACCORDION_ICONS_HTML =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 24 24"><path d="M6 12h6m6 0h-6m0 0V6m0 6v6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 24 24"><path d="M6 12h12" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class ProductList extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.getAttribute('section-id') || '';
      this.paginationMode = this.getAttribute('pagination-mode') || 'paginate';
      this.collectionHandle = this.getAttribute('collection-handle') || '';
      if (this.collectionHandle === 'all') this.collectionHandle = 'all-products';
      this.logoSrc = this.getAttribute('logo-src') || '';
      this.inlineMediaPreviewMode = this.getAttribute('plp-inline-media-preview-mode') === 'true';
      this.inlineMediaAdminUrl = this.getAttribute('plp-inline-media-admin-url') || '';
      this._expandedStorageKey = 'expandedFilters';
      this._hitIndexOffset = 0;
      this._initialRenderDone = false;
      this._shownInlineMedia = new Set();
      this._hitsByKey = new Map();
      this._hitKeySeq = 0;

      this.sidebar = this.querySelector('[data-collection-sidebar]');
      this.gridWrapper = this.querySelector('.product-grid-and-loader-wrapper');
      this.productGrid = this.querySelector('[data-partial="product-grid"]');
      this.productCount = this.querySelector('[data-partial="product-count"]');
      this.activeFilters = this.querySelector('[data-partial="active-filters"]');
      this.filterGroups = this.querySelector('[data-partial="filter-groups"]');
      this.pagination = this.querySelector('[data-partial="pagination"]');
      this.filterCountBadge = this.querySelector('[data-active-filters]');
      this.noResults = this.querySelector('[data-no-results]');
      this.searchPage = this.closest('.search-page');
      this._inlineMedia = this.parseInlineMedia();

      if (this.productGrid) {
        this._incompleteRowObserver = new MutationObserver(() => {
          this.updateIncompleteRow();
          this.updateProductCountDisplay();
        });
        this._incompleteRowObserver.observe(this.productGrid, { childList: true });

        // The 3/4-across toggle only rewrites the grid's inline template.
        this._columnsObserver = new MutationObserver(() => this.onGridColumnsChanged());
        this._columnsObserver.observe(this.productGrid, { attributes: true, attributeFilter: ['style'] });
      }

      if (this.inlineMediaPreviewMode) {
        if (!PLP_PREVIEW) this._inlineMedia = [];
        else if (this._inlineMedia.length) this.showInlineMediaPreviewBanner();
      }

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      document.addEventListener('change', (event) => this.onChange(event));
      document.addEventListener('mousedown', (event) => this.onHandleDown(event));
      document.addEventListener('touchstart', (event) => this.onHandleDown(event), { passive: false });
      document.addEventListener('keyup', (event) => this.onKeyup(event));

      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-action="toggle-sidebar"]')) this.toggleSidebar();
      });

      window.addEventListener('resize', () => {
        this.initRangeSliders();
        this.onGridColumnsChanged();
        this.updateIncompleteRow();
      });
      window.addEventListener('popstate', () => {
        this.runSearch(this.stateFromURL(window.location.href), { pushState: false });
      });

      this.initAccordionState();
      this.initRangeSliders();
      this.initState();
    }

    initState() {
      const configEl = this.querySelector('[data-nosto-search-params]');
      if (!configEl) {
        console.warn('[Nosto PLP] initState: no [data-nosto-search-params] script tag found, aborting');
        return;
      }

      let params;
      try {
        params = JSON.parse(configEl.textContent);
      } catch (error) {
        console.error('[Nosto PLP] initState: failed to JSON.parse config tag', configEl.textContent, error);
        return;
      }

      window.nostojs = window.nostojs || function (cb) {
        (window.nostojs.q = window.nostojs.q || []).push(cb);
      };

      // Arrived via an autocomplete keyword suggestion: flag only the first search as isKeyword, then drop the param
      const landingUrl = new URL(window.location.href);
      if (landingUrl.searchParams.get('nosto_kw') === '1') {
        this._keywordSearch = true;
        landingUrl.searchParams.delete('nosto_kw');
        window.history.replaceState(window.history.state, '', landingUrl.pathname + landingUrl.search + landingUrl.hash);
      }

      const state = this.stateFromURL(window.location.href);
      state.type = params.type;
      state.categoryId = params.categoryId;
      state.categoryPath = params.categoryPath;
      state.size = params.size;
      if (params.type === 'search' && !state.query) state.query = params.query;
      this.state = state;
      if (this.activeFilters) this.renderActiveFilters([], state);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this._searchStarted) return;
          this.runSearch(state, { pushState: false });
        });
      });
    }

    stateFromURL(href) {
      const url = new URL(href, window.location.origin);
      const params = url.searchParams;

      const filters = (params.get('f') || '')
        .split('|')
        .filter(Boolean)
        .map((token) => {
          const [field, value] = token.split(':').map(decodeURIComponent);
          return { field, value };
        });

      return {
        query: params.get('q') || undefined,
        filters,
        sort: params.get('sort_by') || 'manual',
        page: parseInt(params.get('page'), 10) || 1,
      };
    }

    stateToURL(state) {
      const url = new URL(window.location.href);
      const params = url.searchParams;

      params.delete('f');
      params.delete('sort_by');
      params.delete('page');
      params.delete('q');

      if (state.filters && state.filters.length) {
        params.set(
          'f',
          state.filters
            .map(({ field, value }) => `${encodeURIComponent(field)}:${encodeURIComponent(value)}`)
            .join('|')
        );
      }
      if (state.sort && state.sort !== 'manual') params.set('sort_by', state.sort);
      if (state.page && state.page > 1) params.set('page', state.page);
      if (state.query) params.set('q', state.query);

      const search = params.toString();
      return url.pathname + (search ? `?${search}` : '');
    }

    offsetForPage(page, size) {
      return (page - 1) * size;
    }

    registerHit(hit) {
      const key = String(this._hitKeySeq++);
      this._hitsByKey.set(key, { hit, type: this.state.type === 'category' ? 'category' : 'serp' });
      return key;
    }

    nostoSearch(query, options) {
      return new Promise((resolve, reject) => {
        try {
          window.nostojs((api) => {
            if (!api || typeof api.search !== 'function') {
              reject(new Error('nostojs callback fired but api.search is not a function'));
              return;
            }
            api.search(query, options).then(resolve).catch(reject);
          });
        } catch (error) {
          reject(error);
        }
      });
    }

    runSearch(nextState, { pushState = true, append = false } = {}) {
      this._searchStarted = true;
      if ('filters' in nextState || 'sort' in nextState || 'query' in nextState) {
        this._shownInlineMedia = new Set();
      }
      const state = Object.assign({}, this.state, nextState);
      this.state = state;
      this.setLoading(true);

      const from = this.offsetForPage(state.page, state.size);
      const groupedFilters = (state.filters || []).reduce((groups, { field, value }) => {
        const existing = groups.find((group) => group.field === field);
        if (existing) existing.value.push(value);
        else groups.push({ field, value: [value] });
        return groups;
      }, []);
      const query = {
        query: state.query,
        products: {
          categoryId: state.categoryId,
          categoryPath: state.categoryPath,
          facets: ['*'],
          fields: ['name', 'url', 'imageUrl', 'price', 'listPrice'],
          filter: groupedFilters.length ? groupedFilters : undefined,
          sort: SORT_MAP[state.sort],
          size: state.size,
          from,
        },
      };
      const options = state.type === 'category' ? { track: 'category', redirect: true } : { track: 'serp' };
      if (this._keywordSearch && state.type !== 'category') {
        options.isKeyword = true;
        this._keywordSearch = false;
      }

      this.nostoSearch(query, options)
        .then((response) => {
          if (response && response.redirect) {
            window.location.href = response.redirect;
            return;
          }
          const products = (response && response.products) || {};
          const total = products.total || 0;
          this.logHits(products.hits || [], { page: state.page, append });
          this._lastTotal = total;
          this.renderGrid(products.hits || [], { append });
          this.renderCount(total);
          this.renderFacets(products.facets || [], state);
          this.renderPagination(total, state.size, state.page);
          this.renderEmptyState(total);
          if (!append && pushState) this.scrollToGridTop();
          if (pushState) window.history.pushState({}, '', this.stateToURL(state));
        })
        .catch((error) => {
          console.error('[Nosto PLP] api.search rejected/failed', error);
        })
        .finally(() => this.setLoading(false));

      setTimeout(() => {
        if (this.classList.contains('is-loading')) {
          console.error('[Nosto PLP] search still pending 8s after being queued — window.nostojs never resolved');
        }
      }, 8000);
    }

    logHits(hits, { page, append } = {}) {
      log(`[Nosto PLP] hits for page ${page}${append ? ' (appended)' : ''}: ${hits.length}`);
      logTable(hits.map((hit, index) => ({ index, name: hit.name, url: hit.url })));
    }

    elementFromHTML(html) {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      return template.content.firstElementChild;
    }

    createTileLoader(hit, { index, priority = false } = {}) {
      const isEssential = priority || (index !== undefined && index < ESSENTIAL_TILE_COUNT);
      const priorityAttr = priority ? ' data-priority="true"' : isEssential ? ' data-eager="true"' : '';
      const innerHTML = isEssential ? this.fastTileHTML(hit, { priority }) : this.tileSkeletonHTML();
      const hitKey = this.registerHit(hit);

      return this.elementFromHTML(
        `<intersection-loader data-url="${this.escape(this.tileFetchUrl(hit.url))}" data-section-id="nosto-product-card-full" data-root-margin="0px" data-extra="hit-key:${this.escape(hitKey)}"${priorityAttr}>${innerHTML}</intersection-loader>`
      );
    }

    tileFetchUrl(hitUrl) {
      if (!this.collectionHandle) return hitUrl;
      const productHandle = this.handleFromUrl(hitUrl);
      return productHandle ? `/collections/${this.collectionHandle}/products/${productHandle}` : hitUrl;
    }

    productSrcset(imageUrl) {
      return IMAGE_WIDTHS.map((width) => `${this.shopifyImageUrl(imageUrl, width)} ${width}w`).join(', ');
    }

    fastTileHTML(hit, { priority = false } = {}) {
      if (!hit.imageUrl) return this.tileSkeletonHTML();

      const src = this.shopifyImageUrl(hit.imageUrl, 600);
      const srcset = this.productSrcset(hit.imageUrl);
      const loadingAttrs = priority ? 'loading="eager" fetchpriority="high"' : 'loading="eager"';

      return `
        <div class="sp-flex sp-flex-col">
          <div data-product-card-media class="sp-block sp-w-full sp-aspect-[1/1.5] sp-relative sp-z-0">
            <a href="${this.escape(hit.url)}" aria-label="${this.escape(hit.name)}" class="sp-absolute sp-inset-0 sp-block">
              <img
                src="${this.escape(src)}"
                srcset="${this.escape(srcset)}"
                sizes="(min-width: 750px) 25vw, 50vw"
                width="600"
                height="900"
                alt="${this.escape(hit.name)}"
                ${loadingAttrs}
                class="sp-object-cover sp-w-full sp-h-full"
              >
            </a>
          </div>
          <div class="sp-pt-[6px] sp-pb-[14px] sp-px-[10px]" data-product-card-info>
            <div class="sp-flex sp-items-start sp-justify-between sp-gap-[4px]">
              <a class="sp-text-[15px]" href="${this.escape(hit.url)}">${this.escape(hit.name)}</a>
              <span class="sp-h-[15px] sp-w-[20%] sp-shrink-0 sp-rounded-[2px] ${PLACEHOLDER_CLASS}" data-tile-price></span>
            </div>
          </div>
        </div>
      `;
    }

    shopifyImageUrl(url, width) {
      return `${url}${url.indexOf('?') === -1 ? '?' : '&'}width=${width}`;
    }

    renderGrid(hits, { append = false } = {}) {
      if (!append) {
        this._hitIndexOffset = 0;
        this._hitsByKey.clear();
      }
      if (!this.productGrid) {
        console.error('[Nosto PLP] renderGrid: product grid element not found');
        return;
      }

      if (!append && !this._initialRenderDone) {
        this._initialRenderDone = true;
        this.populateInitialGrid(hits);
        this._hitIndexOffset += hits.filter((hit) => hit.url).length;
        return;
      }
      this._initialRenderDone = true;

      const validHits = hits.filter((hit) => hit.url);
      const tiles = validHits.map((hit, index) =>
        this.createTileLoader(hit, { index: this._hitIndexOffset + index, priority: !append && index === 0 })
      );
      this._hitIndexOffset += validHits.length;

      this.spliceInlineMedia(tiles, this.state.page);

      if (append) {
        const fragment = document.createDocumentFragment();
        tiles.forEach((tile) => fragment.appendChild(tile));
        this.productGrid.appendChild(fragment);
        return;
      }

      this.replaceGridChildren(tiles);
    }

    scrollToGridTop() {
      const headerHeight = document.querySelector('.nili-header-container')?.offsetHeight || 0;
      const top = this.gridWrapper.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    populateInitialGrid(hits) {
      const validHits = hits.filter((hit) => hit.url);
      const page = this.state.page;

      // Inline-media entries targeting one of the essential/LCP slots (position 1-4)
      // take that slot directly, same as a real product hit would — rather than
      // being deferred until after the essential zone.
      // Two entries can target the same position (e.g. a collection-specific tile
      // and a global one) — each gets its own slot, bumping to the next free one,
      // so the overflow doesn't fall through to the post-essential promotion.
      const essentialMedia = [];
      const takenSlots = new Set();
      this._inlineMedia
        .map((entry, i) => ({ entry, i }))
        .filter(({ entry, i }) => !this._shownInlineMedia.has(i))
        .filter(({ entry }) => entry.page === null || entry.page === page)
        .filter(({ entry }) => Number.isFinite(entry.position) && entry.position >= 1 && entry.position <= ESSENTIAL_TILE_COUNT)
        .sort((a, b) => a.entry.position - b.entry.position)
        .forEach(({ entry, i }) => {
          let slot = entry.position;
          while (takenSlots.has(slot) && slot <= ESSENTIAL_TILE_COUNT) slot++;
          if (slot > ESSENTIAL_TILE_COUNT) return;
          takenSlots.add(slot);
          essentialMedia.push({ entry, i, slot });
        });

      const essentialHitCount = ESSENTIAL_TILE_COUNT - essentialMedia.length;
      const essentialHits = validHits.slice(0, essentialHitCount);
      const remainingHits = validHits.slice(essentialHitCount);

      const existingTiles = Array.from(this.productGrid.children);
      let hitIndex = 0;
      let consumedSlots = 0;
      for (let slot = 0; slot < ESSENTIAL_TILE_COUNT; slot++) {
        const tile = existingTiles[slot];
        if (!tile) break;
        consumedSlots++;

        const media = essentialMedia.find((m) => m.slot === slot + 1);
        if (media) {
          tile.replaceWith(this.inlineMediaNode(media.entry));
          this._shownInlineMedia.add(media.i);
          continue;
        }

        const hit = essentialHits[hitIndex];
        if (!hit) continue;
        this.populateTileFromHit(tile, hit, { priority: slot === 0 });
        this.upgradeEssentialTile(tile, hit, slot);
        hitIndex++;
      }

      const remainingTiles = remainingHits.map((hit, index) =>
        this.createTileLoader(hit, { index: index + consumedSlots })
      );
      this.spliceInlineMedia(remainingTiles, page, consumedSlots);
      this.replaceGridChildren(remainingTiles, consumedSlots);
    }

    populateTileFromHit(tile, hit, { priority = false } = {}) {
      const media = tile.querySelector('[data-product-card-media]');
      const titleEl = tile.querySelector('[data-tile-link]');

      const handle = this.handleFromUrl(hit.url);
      if (handle) tile.setAttribute('data-handle', handle);
      tile.setAttribute('data-hit-key', this.registerHit(hit));

      if (media && hit.imageUrl) {
        const src = this.shopifyImageUrl(hit.imageUrl, 600);
        const srcset = this.productSrcset(hit.imageUrl);
        const loadingAttrs = priority ? 'loading="eager" fetchpriority="high"' : 'loading="eager"';
        media.insertAdjacentHTML(
          'beforeend',
          `<a href="${this.escape(hit.url)}" aria-label="${this.escape(hit.name || '')}" data-product-card-link class="sp-absolute sp-inset-0 sp-block">
            <div data-product-card-slide class="sp-absolute sp-top-0 sp-left-0 sp-w-full sp-h-full">
              <img
                src="${this.escape(src)}"
                srcset="${this.escape(srcset)}"
                sizes="(min-width: 750px) 25vw, 50vw"
                width="600"
                height="900"
                alt="${this.escape(hit.name || '')}"
                ${loadingAttrs}
                class="sp-transition-opacity sp-ease-linear sp-object-cover sp-w-full sp-h-full sp-opacity-0"
              >
            </div>
          </a>`
        );

        const photoImg = media.querySelector('[data-product-card-slide] img');
        const logoImg = media.querySelector('[data-product-card-logo]');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (photoImg) photoImg.classList.replace('sp-opacity-0', 'sp-opacity-100');
            if (logoImg) logoImg.classList.replace('sp-opacity-10', 'sp-opacity-0');
          });
        });
      }

      if (titleEl && hit.name) {
        titleEl.textContent = hit.name;
        titleEl.classList.remove('sp-bg-[#f7f7f7]');
      }

      if (typeof tile.refresh === 'function') tile.refresh();
    }

    async upgradeEssentialTile(tile, hit, index = 0) {
      const targetUrl = this.tileFetchUrl(hit.url);
      const fetchUrl = `${targetUrl}${targetUrl.indexOf('?') === -1 ? '?' : '&'}section_id=nosto-product-card-full`;
      try {
        const response = await fetch(fetchUrl, { priority: 'low' });
        if (!response.ok) throw new Error(`fetch failed with status ${response.status}`);
        if (!tile.isConnected) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('#shopify-section-nosto-product-card-full');
        const fetchedCard = content ? content.querySelector('product-card') : null;
        if (!fetchedCard || !tile.isConnected) return;

        const clGroup = fetchedCard.getAttribute('data-cl-group');

        const priceEl = tile.querySelector('[data-tile-price]');
        const fetchedPrice = fetchedCard.querySelector('[data-tile-price]');
        if (priceEl && fetchedPrice) priceEl.replaceWith(fetchedPrice);

        const badgeContainer = tile.querySelector('[data-badge-container]');
        const fetchedBadge = fetchedCard.querySelector('[data-badge-container]');
        if (badgeContainer && fetchedBadge) {
          badgeContainer.replaceChildren(...fetchedBadge.childNodes);
        }
        this.applyLeadAsset(tile, fetchedCard, index);
        this.applyGalleryImages(tile, fetchedCard);
        this.applyNavControls(tile, fetchedCard);

        const swatchContainer = tile.querySelector('[data-cl-swatch-container]');
        if (clGroup) {
          const fetchedSwatches = fetchedCard.querySelector('[data-cl-swatch-container]');
          if (swatchContainer && fetchedSwatches) {
            swatchContainer.replaceChildren(...fetchedSwatches.childNodes);
          }
          tile.setAttribute('data-cl-group', clGroup);
        } else if (swatchContainer) {
          swatchContainer.replaceChildren();
        }

        if (typeof tile.refresh === 'function') tile.refresh();
      } catch (error) {
        console.error('[Nosto PLP] upgradeEssentialTile: fetch failed for', hit.url, error);
      }
    }

    applyLeadAsset(tile, fetchedCard, index = 0) {
      const raw = fetchedCard.getAttribute('data-debug-lead-asset') || '';
      const [url, isVideoRaw] = raw.split('|');
      if (!url) return;

      const link = tile.querySelector('[data-product-card-link]');
      const existingSlide = link && link.querySelector('[data-product-card-slide]');
      const existingImg = existingSlide && existingSlide.querySelector('img');
      if (!link || !existingSlide || !existingImg) return;

      const staggerDelay = index * LEAD_ASSET_STAGGER_MS;

      const mediaClassName = existingImg.className;
      existingImg.classList.add('sp-duration-300');
      existingImg.style.transitionDelay = `${staggerDelay}ms`;
      existingImg.classList.replace('sp-opacity-100', 'sp-opacity-0');

      const leadSlide = document.createElement('div');
      leadSlide.setAttribute('data-product-card-slide', '');
      leadSlide.className = existingSlide.className;

      const isVideo = isVideoRaw === 'true';
      const media = document.createElement(isVideo ? 'video' : 'img');
      media.className = `${mediaClassName.replace('sp-opacity-100', 'sp-opacity-0')} sp-duration-300`;
      media.style.transitionDelay = `${staggerDelay + 100}ms`;
      media.src = url;
      if (isVideo) {
        media.autoplay = true;
        media.loop = true;
        media.muted = true;
        media.playsInline = true;
      } else {
        media.alt = existingImg.alt;
        media.width = 600;
        media.height = 900;
        if (url.indexOf('cdn.shopify.com') !== -1) {
          media.src = this.shopifyImageUrl(url, 900);
          media.srcset = this.productSrcset(url);
          media.sizes = '(min-width: 750px) 25vw, 50vw';
        }
      }
      leadSlide.appendChild(media);

      link.insertBefore(leadSlide, existingSlide);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          media.classList.replace('sp-opacity-0', 'sp-opacity-100');
        });
      });
    }

    applyGalleryImages(tile, fetchedCard) {
      const link = tile.querySelector('[data-product-card-link]');
      if (!link) return;

      const coveredCount = link.querySelectorAll('[data-product-card-slide]').length;
      const extraSlides = Array.from(fetchedCard.querySelectorAll('[data-product-card-slide]')).slice(coveredCount);
      extraSlides.forEach((slide) => link.appendChild(slide));
    }

    applyNavControls(tile, fetchedCard) {
      const media = tile.querySelector('[data-product-card-media]');
      const fetchedMedia = fetchedCard.querySelector('[data-product-card-media]');
      if (media && fetchedMedia) {
        media.classList.toggle('has-single-image', fetchedMedia.classList.contains('has-single-image'));
      }
      if (media && fetchedMedia && !media.querySelector('button[data-action]')) {
        fetchedMedia.querySelectorAll(':scope > button[data-action]').forEach((button) => media.appendChild(button));
      }

      const link = tile.querySelector('[data-product-card-link]');
      const fetchedPagination = fetchedCard.querySelector('[data-pagination-outer]');
      if (link && fetchedPagination && !link.querySelector('[data-pagination-outer]')) {
        link.appendChild(fetchedPagination);
      }
    }

    handleFromUrl(url) {
      if (!url) return '';
      const match = url.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : '';
    }

    replaceGridChildren(tiles, startIndex = 0) {
      const existing = Array.from(this.productGrid.children).slice(startIndex);
      let index = 0;

      const insertNext = () => {
        const oldTile = existing[index];
        if (oldTile) oldTile.replaceWith(tiles[index]);
        else this.productGrid.appendChild(tiles[index]);
        index += 1;

        if (index < tiles.length) {
          requestAnimationFrame(insertNext);
        } else if (existing.length > tiles.length) {
          existing.slice(tiles.length).forEach((tile) => tile.remove());
        }
      };

      if (tiles.length) insertNext();
      else existing.forEach((tile) => tile.remove());
    }

    parseInlineMedia() {
      const el = this.querySelector('[data-plp-inline-media]');
      if (!el) return [];
      try {
        const parsed = JSON.parse(el.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('[Nosto PLP] parseInlineMedia: failed to JSON.parse config tag', el.textContent, error);
        return [];
      }
    }

    spliceInlineMedia(tiles, page, positionOffset = 0) {
      if (!this._inlineMedia.length) return;

      const entries = this._inlineMedia
        .map((entry, i) => ({ entry, i }))
        .filter(({ entry, i }) => {
          if (this._shownInlineMedia.has(i)) return false;
          return entry.page === null || entry.page === page;
        })
        .filter(({ entry }) => Number.isFinite(entry.position) && entry.position >= 1)
        .sort((a, b) => a.entry.position - b.entry.position);

      // Slots that fall within the essential/skeleton-rendered tiles (positionOffset)
      // can't be spliced in there without disturbing the LCP-critical first paint —
      // bunch them immediately after that protected zone instead of dropping them.
      let promoted = 0;
      entries.forEach(({ entry, i }) => {
        const rawIndex = entry.position - 1 - positionOffset;
        const index = rawIndex < 0 ? promoted++ : rawIndex + promoted;
        if (index > tiles.length) return;
        tiles.splice(index, 0, this.inlineMediaNode(entry));
        this._shownInlineMedia.add(i);
      });
    }

    inlineMediaStyle(entry) {
      // Spans are authored for the default column count, but the 3/4-across
      // toggle and the tablet breakpoint change it. A span wider than the grid
      // makes the browser add an implicit extra column, so clamp to what exists.
      const columns = this.currentColumnCount();
      const widthDesktop = Math.min(entry.widthDesktop || 1, columns);
      const widthMobile = Math.min(entry.widthMobile || 1, columns);
      const heightDesktop = entry.heightDesktop || 1;
      const heightMobile = entry.heightMobile || 1;

      // A row-spanning tile only needs an aspect-ratio fallback when it's
      // guaranteed to be alone in its row band (its own width span fills every
      // column) — otherwise leave it `auto` and let grid stretch fill whatever
      // height its row-sharing siblings establish. align-self:stretch does NOT
      // reliably override an explicit aspect-ratio, so this must be exact rather
      // than "set a ratio and hope stretch wins".
      const fallbackRatio = (span, row) => span / (row * 1.5);
      const isFullWidthDesktop = widthDesktop >= columns;
      const isFullWidthMobile = widthMobile >= columns;
      const desktopRatio = isFullWidthDesktop ? entry.aspectRatio || fallbackRatio(widthDesktop, heightDesktop) : 'auto';
      const mobileRatio = isFullWidthMobile ? entry.aspectRatio || fallbackRatio(widthMobile, heightMobile) : 'auto';

      return `--plp-inline-media-span-desktop:${widthDesktop};--plp-inline-media-span-mobile:${widthMobile};--plp-inline-media-row-desktop:${heightDesktop};--plp-inline-media-row-mobile:${heightMobile};--plp-inline-media-aspect-ratio-desktop:${desktopRatio};--plp-inline-media-aspect-ratio-mobile:${mobileRatio}${isFullWidthDesktop ? ';--plp-inline-media-inset-desktop:0px' : ''}${isFullWidthMobile ? ';--plp-inline-media-inset-mobile:0px' : ''}`;
    }

    onGridColumnsChanged() {
      const columns = this.currentColumnCount();
      if (columns === this._lastColumnCount) return;
      this._lastColumnCount = columns;
      this.relayoutInlineMedia();
      this.updateIncompleteRow();
    }

    relayoutInlineMedia() {
      if (!this.productGrid) return;
      this.productGrid.querySelectorAll('[data-plp-inline-media]').forEach((node) => {
        if (node._plpInlineEntry) node.setAttribute('style', this.inlineMediaStyle(node._plpInlineEntry));
      });
    }

    inlineMediaNode(entry) {
      const tag = entry.link ? 'a' : 'div';
      const spanStyle = this.inlineMediaStyle(entry);
      const linkAttrs = entry.link
        ? ` href="${this.escape(entry.link)}" class="sp-block sp-relative sp-w-full sp-overflow-hidden focus-visible:sp-outline focus-visible:sp-outline-2 focus-visible:sp-outline-black focus-visible:-sp-outline-offset-[5px]"`
        : ' class="sp-block sp-relative sp-w-full sp-overflow-hidden"';

      const mediaClass = 'sp-absolute sp-top-0 sp-left-0 sp-w-full sp-h-full sp-object-cover';
      const media = entry.isVideo
        ? `<video class="${mediaClass}" src="${this.escape(entry.url)}" autoplay loop muted playsinline></video>`
        : `<img class="${mediaClass}" src="${this.escape(entry.url)}" loading="lazy" alt="">`;

      const debugBadge = this.inlineMediaPreviewMode
        ? `<span class="sp-absolute sp-top-[6px] sp-left-[6px] sp-z-10 sp-bg-red-600 sp-text-white sp-text-[11px] sp-font-bold sp-uppercase sp-leading-none sp-px-[6px] sp-py-[4px] sp-pointer-events-none">Slot ${entry.page ? `${entry.page}-` : ''}${entry.position}</span>`
        : '';

      const node = this.elementFromHTML(
        `<${tag} data-plp-inline-media${linkAttrs} style="${spanStyle}">${media}${debugBadge}</${tag}>`
      );
      node._plpInlineEntry = entry;
      return node;
    }

    showInlineMediaPreviewBanner() {
      if (this._inlineMediaPreviewBannerShown) return;
      this._inlineMediaPreviewBannerShown = true;

      const linkHTML = this.inlineMediaAdminUrl
        ? ` <a href="${this.escape(this.inlineMediaAdminUrl)}" target="_blank" rel="noopener" class="sp-underline">Edit in Shopify Admin</a>`
        : '';
      const banner = this.elementFromHTML(
        `<div class="sp-bg-red-600 sp-text-white sp-text-[13px] sp-font-bold sp-uppercase sp-text-center sp-py-[8px] sp-px-[12px]">In-grid preview mode — only visible with ?preview=true.${linkHTML}</div>`
      );
      (this.gridWrapper || this).prepend(banner);
    }

    tileSkeletonHTML() {
      let logo = '';
      if (this.logoSrc) {
        const sep = this.logoSrc.includes('?') ? '&' : '?';
        const atWidth = (width) => `${this.logoSrc}${sep}width=${width}`;
        const srcset = [450, 675, 900, 1350, 1673].map((width) => `${atWidth(width)} ${width}w`).join(', ');
        logo = `<img data-product-card-logo src="${atWidth(1350)}" srcset="${srcset}" sizes="(min-width: 750px) 35vw, 60vw" width="1115" height="1300" alt="" aria-hidden="true" loading="lazy" class="sp-absolute sp-top-[-15%] sp-left-[-15%] sp-w-[130%] sp-max-w-none sp-h-[130%] sp-object-cover sp-opacity-10 sp-pointer-events-none">`;
      }
      return `
        <div class="sp-flex sp-flex-col">
          <div data-product-card-media class="sp-w-full sp-aspect-[1/1.5] sp-relative sp-overflow-hidden ${PLACEHOLDER_CLASS}">${logo}</div>
          <div class="sp-pt-[6px] sp-pb-[14px] sp-px-[10px]" data-product-card-info>
            <div class="sp-flex sp-items-start sp-justify-between sp-gap-[4px]">
              <span class="sp-h-[15px] sp-w-[60%] sp-rounded-[2px] ${PLACEHOLDER_CLASS}" data-tile-link></span>
              <span class="sp-h-[15px] sp-w-[20%] sp-shrink-0 sp-rounded-[2px] ${PLACEHOLDER_CLASS}" data-tile-price></span>
            </div>
          </div>
        </div>
      `;
    }

    renderCount(total) {
      if (!this.productCount) return;
      if (this.searchPage) {
        const term = (this.state.query || '').trim();
        const label = total === 1 ? '1 RESULT' : `${total} RESULTS`;
        this.productCount.textContent = term ? `${label} FOR: ${term.toUpperCase()}` : label;
        this.productCount.classList.remove('sp-hidden');
        return;
      }
      this.productCount.textContent = total === 1 ? '1 product' : `${total} products`;
    }

    renderEmptyState(total) {
      if (this.noResults) this.noResults.classList.toggle('hidden', total !== 0);
      if (this.productGrid) this.productGrid.classList.toggle('hidden', total === 0);
      if (this.searchPage) this.searchPage.classList.toggle('search-page--no-results', total === 0);
    }

    renderFacets(facets, state) {
      if (this.filterGroups) {
        this.filterGroups.innerHTML = facets.map((facet) => this.facetGroupHTML(facet, state)).join('');
      }
      if (this.activeFilters) this.renderActiveFilters(facets, state);
      this.initAccordionState();
    }

    facetGroupHTML(facet, state) {
      const values = facet.data || [];
      const activeCount = (state.filters || []).filter((filter) => filter.field === facet.field).length;
      const countBadge = activeCount > 0 ? `<span>(${activeCount})</span>` : '';

      const items = values
        .map((value, index) => {
          const selected = (state.filters || []).some(
            (filter) => filter.field === facet.field && filter.value === value.value
          );
          const hidden = index >= FACET_VISIBLE_LIMIT;
          const disabled = value.count === 0 && !selected;
          const id = `facet-${this.handleize(facet.field)}-${this.handleize(String(value.value))}`;
          const stateClass = selected
            ? ' sidebar__item--active link--remove'
            : disabled
              ? ' link--disable'
              : ' link--add';

          return `
            <li class="sidebar__item${stateClass}${hidden ? ' is-hidden' : ''}"${hidden ? ' data-link-hidden' : ''}>
              <input type="checkbox" id="${id}" data-facet-field="${this.escape(facet.field)}" data-facet-value="${this.escape(String(value.value))}"${selected ? ' checked' : ''}${disabled ? ' disabled' : ''}>
              <label for="${id}">${this.escape(String(value.value))} (${value.count})</label>
            </li>
          `;
        })
        .join('');

      const moreOptions =
        values.length > FACET_VISIBLE_LIMIT
          ? '<div class="collection__sidebar__actions"><button type="button" data-action="show-more" class="collection__sidebar__link">More options</button></div>'
          : '';

      return `
        <div class="collection__sidebar__group">
          <p class="collection__sidebar__heading" data-action="toggle-accordion" tabindex="0" data-focus-element>
            <span>${this.escape(facet.name)}</span>${countBadge}${ACCORDION_ICONS_HTML}
          </p>
          <ul class="collection-nav" role="group">${items}</ul>
          ${moreOptions}
        </div>
      `;
    }

    renderActiveFilters(facets, state) {
      const filters = state.filters || [];

      if (this.filterCountBadge) {
        this.filterCountBadge.textContent = filters.length;
        this.filterCountBadge.classList.toggle('hidden', filters.length < 1);
      }

      if (!filters.length) {
        this.activeFilters.innerHTML = '';
        return;
      }

      const clearAll =
        filters.length > 1
          ? '<a href="#" class="active__filters__clear" data-action="filter-clear-all"><small>Clear filters</small></a>'
          : '';

      const chips = filters
        .map(({ field, value }) => {
          const facet = facets.find((f) => f.field === field);
          const facetValue = facet && facet.data && facet.data.find((d) => d.value === value);
          const label = facetValue ? String(facetValue.value) : value;
          return `<a href="#" class="active__filters__remove" data-action="filter-update" data-facet-field="${this.escape(field)}" data-facet-value="${this.escape(value)}"><small>${this.escape(label)}</small><span class="filter__x">&times;</span></a>`;
        })
        .join('');

      this.activeFilters.innerHTML = clearAll + chips;
    }

    toggleFilter(field, value) {
      const filters = (this.state && this.state.filters) || [];
      const existingIndex = filters.findIndex((filter) => filter.field === field && filter.value === value);
      const nextFilters =
        existingIndex === -1 ? filters.concat([{ field, value }]) : filters.filter((_, i) => i !== existingIndex);
      this.runSearch({ filters: nextFilters, page: 1 });
    }

    renderPagination(total, size, page) {
      if (!this.pagination) return;

      const totalPages = Math.max(1, Math.ceil(total / size));
      if (totalPages <= 1) {
        this.pagination.innerHTML = '';
        return;
      }

      if (this.paginationMode === 'load-more') {
        if (page >= totalPages) {
          this.pagination.innerHTML = '';
          return;
        }
        this.pagination.innerHTML = `
          <div class="load-more-section">
            <button type="button" class="load-more-btn" data-action="load-more">Load More</button>
            <p class="product-count-display loaded">
              <span class="current-products-count">${Math.min(page * size, total)}</span> of
              <span class="total-products-count">${total}</span>
            </p>
          </div>
        `;
        return;
      }

      const arrowIcon = (direction) =>
        direction === 'prev'
          ? '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-nav-arrow-left" viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-nav-arrow-right" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const arrow = (direction, targetPage, enabled) => {
        const icon = arrowIcon(direction);
        if (!enabled) {
          return `
            <li class="pagination-custom__arr disabled">
              <span>${icon}<span class="visually-hidden">${page}</span></span>
            </li>
          `;
        }
        return `
          <li class="pagination-custom__arr">
            <button type="button" data-action="paginate" data-page="${targetPage}">${icon}<span class="visually-hidden">Page ${targetPage}</span></button>
          </li>
        `;
      };

      const WINDOW = 2;
      const shownPages = new Set([1, totalPages]);
      for (let i = page - WINDOW; i <= page + WINDOW; i += 1) {
        if (i >= 1 && i <= totalPages) shownPages.add(i);
      }
      const sortedPages = Array.from(shownPages).sort((a, b) => a - b);

      let numberItems = '';
      let previousPage = 0;
      sortedPages.forEach((pageNumber) => {
        if (pageNumber - previousPage > 1) {
          numberItems += '<li class="pagination-custom__num pagination-custom__num--dots"><span>...</span></li>';
        }
        numberItems += `
          <li class="pagination-custom__num${pageNumber === page ? ' active' : ''}">
            <button type="button" data-action="paginate" data-page="${pageNumber}">${pageNumber}</button>
          </li>
        `;
        previousPage = pageNumber;
      });

      this.pagination.innerHTML = `
        <ul class="pagination-custom">
          ${arrow('prev', page - 1, page > 1)}
          <li class="pagination-custom__spacer"><span>&#8203;</span></li>
          ${numberItems}
          <li class="pagination-custom__spacer"><span>&#8203;</span></li>
          ${arrow('next', page + 1, page < totalPages)}
        </ul>
      `;
    }

    updateIncompleteRow() {
      if (!this.productGrid) return;

      const items = Array.from(this.productGrid.children);
      items.forEach((item) => item.classList.remove('incomplete-row-hidden'));

      const { page, size } = this.state || {};
      if (!page || !size || !this._lastTotal) return;
      const hasMorePages = page < Math.ceil(this._lastTotal / size);
      if (!hasMorePages || !items.length) return;

      const columns = this.currentColumnCount();
      if (columns <= 1) return;

      let totalSpan = items.reduce((sum, item) => sum + this.itemSpan(item), 0);
      if (totalSpan <= columns || totalSpan % columns === 0) return;

      const hidden = [];
      for (let i = items.length - 1; i >= 0 && totalSpan % columns !== 0; i--) {
        hidden.push(items[i]);
        totalSpan -= this.itemSpan(items[i]);
      }
      hidden.forEach((item) => item.classList.add('incomplete-row-hidden'));
    }

    updateProductCountDisplay() {
      if (this.paginationMode !== 'load-more' || !this.pagination || !this.productGrid) return;
      const countEl = this.pagination.querySelector('.current-products-count');
      if (!countEl) return;

      const displayedCount = Array.from(this.productGrid.children).filter(
        (item) => !item.classList.contains('incomplete-row-hidden') && !item.hasAttribute('data-plp-inline-media')
      ).length;
      countEl.textContent = displayedCount;
    }

    currentColumnCount() {
      // Read the *explicit* column count. Counting computed tracks would also
      // count implicit ones that an over-wide span creates. The grid toggle sets
      // an inline template; otherwise the section's per-breakpoint variable wins.
      const repeatCount = (value) => {
        const match = /repeat\(\s*(\d+)/.exec(value || '');
        return match ? parseInt(match[1], 10) : 0;
      };
      const fromToggle = repeatCount(this.productGrid.style.gridTemplateColumns);
      if (fromToggle) return fromToggle;

      const varName = window.innerWidth >= 1024 ? '--grid' : window.innerWidth >= 750 ? '--grid-tablet' : '--grid-small';
      const fromVar = repeatCount(getComputedStyle(this.productGrid).getPropertyValue(varName));
      if (fromVar) return fromVar;

      const columns = getComputedStyle(this.productGrid).gridTemplateColumns.split(' ').filter(Boolean).length;
      return columns || 1;
    }

    parseSpan(startValue, endValue) {
      const match = /^span\s+(\d+)/.exec(startValue) || /^span\s+(\d+)/.exec(endValue);
      return match ? parseInt(match[1], 10) : 1;
    }

    itemSpan(item) {
      const style = getComputedStyle(item);
      const colSpan = this.parseSpan(style.gridColumnStart, style.gridColumnEnd);
      const rowSpan = this.parseSpan(style.gridRowStart, style.gridRowEnd);
      return colSpan * rowSpan;
    }

    onClick(event) {
      const tileEl = event.target.closest('[data-hit-key]');
      if (tileEl) {
        const entry = this._hitsByKey.get(tileEl.dataset.hitKey);
        if (entry) window.nostojs((api) => api.recordSearchClick(entry.type, entry.hit));
      }

      const actionElement = event.target.closest('[data-action]');
      if (!actionElement) return;

      const { action } = actionElement.dataset;

      switch (action) {
        case 'filter-update': {
          event.preventDefault();
          const { facetField, facetValue } = actionElement.dataset;
          if (facetField !== undefined) this.toggleFilter(facetField, facetValue);
          break;
        }
        case 'filter-clear-all':
          event.preventDefault();
          this.runSearch({ filters: [], page: 1 });
          break;
        case 'paginate':
          event.preventDefault();
          this.runSearch({ page: parseInt(actionElement.dataset.page, 10) || 1 });
          break;
        case 'load-more':
          event.preventDefault();
          this.runSearch({ page: (this.state.page || 1) + 1 }, { append: true, pushState: false });
          break;
        case 'show-more':
          event.preventDefault();
          this.onShowMore(actionElement);
          break;
        case 'close-sidebar':
          event.preventDefault();
          this.closeSidebar();
          break;
        case 'clear-search': {
          event.preventDefault();
          const form = actionElement.closest('[data-search-form]');
          const input = form && form.querySelector('[data-search-page-input]');
          if (input) input.value = '';
          this.runSearch({ query: '', page: 1 });
          break;
        }
        case 'toggle-accordion':
          event.preventDefault();
          this.toggleAccordionGroup(actionElement);
          break;
        default:
          break;
      }
    }

    onKeyup(event) {
      if (event.key === 'Escape') {
        this.closeSidebar();
        return;
      }
      if (event.key === ' ' || event.key === 'Enter') {
        const heading = event.target.closest('[data-action="toggle-accordion"]');
        if (heading) {
          event.preventDefault();
          this.toggleAccordionGroup(heading);
        }
      }
    }

    onChange(event) {
      const sortRadio = event.target.closest('[data-sort-radio]');
      if (sortRadio) {
        this.onSortChange(sortRadio);
        return;
      }

      const facetCheckbox = event.target.closest('input[type="checkbox"][data-facet-field]');
      if (facetCheckbox) {
        this.toggleFilter(facetCheckbox.dataset.facetField, facetCheckbox.dataset.facetValue);
      }
    }

    onSortChange(radio) {
      this.runSearch({ sort: radio.value, page: 1 });
    }

    onSubmit(event) {
      const form = event.target.closest('[data-search-form]');
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector('[data-search-page-input]');
      this.runSearch({ query: input ? input.value.trim() : '', page: 1 });
    }

    onShowMore(link) {
      const group = link.closest('.collection__sidebar__actions');
      if (!group) return;
      group.style.display = 'none';

      const list = group.previousElementSibling;
      if (list) {
        list.querySelectorAll('[data-link-hidden]').forEach((item) => {
          item.classList.remove('is-hidden');
          item.removeAttribute('data-link-hidden');
        });
      }

      const headingSpan = group.parentElement
        ? group.parentElement.querySelector('[data-action="toggle-accordion"] span')
        : null;
      const headingText = headingSpan ? headingSpan.textContent.trim() : '';
      if (headingText) {
        const expanded = this.getExpandedGroups();
        if (expanded.indexOf(headingText) === -1) {
          expanded.push(headingText);
          localStorage.setItem(this._expandedStorageKey, JSON.stringify(expanded));
        }
      }
    }

    isSidebarOpen() {
      return !!this.sidebar && this.sidebar.getAttribute('data-open') === 'true';
    }

    toggleSidebar() {
      this.setSidebarOpen(!this.isSidebarOpen());
    }

    closeSidebar() {
      this.setSidebarOpen(false);
    }

    setSidebarOpen(isOpen) {
      if (!this.sidebar) return;
      this.sidebar.setAttribute('data-open', String(isOpen));
      document.querySelectorAll('[data-aria-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(isOpen)));

      // Desktop sidebar is inline, not an overlay — nothing to lock.
      if (!window.matchMedia('(max-width: 767px)').matches) return;

      // body-scroll-lock via the theme's own plumbing; bare overflow:hidden does not hold on iOS.
      if (isOpen) {
        const scrollable = this.sidebar.querySelector('.collection__sidebar') || this.sidebar;
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: scrollable }));
      } else {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      }
    }

    toggleAccordionGroup(heading) {
      const isOpening = !heading.classList.contains('is-active');

      this.setAccordionGroup(heading, isOpening);
      this.persistExpanded(heading, isOpening);
    }

    setAccordionGroup(heading, open, animate = true) {
      heading.classList.toggle('is-active', open);
      const list = heading.nextElementSibling;
      const actions =
        list && list.nextElementSibling && list.nextElementSibling.classList.contains('collection__sidebar__actions')
          ? list.nextElementSibling
          : null;
      this.setCollapsible(list, open, animate);
      if (actions) this.setCollapsible(actions, open, animate);
    }

    setCollapsible(el, open, animate) {
      if (!el) return;
      el.style.overflow = 'hidden';
      el.style.transitionProperty = 'max-height';
      el.style.transitionTimingFunction = 'ease';
      el.style.transitionDuration = animate ? '0.3s' : '0s';

      if (open) {
        el.style.removeProperty('display');
        requestAnimationFrame(() => {
          el.style.maxHeight = '2000px';
        });
      } else {
        el.style.maxHeight = '0px';
        const finish = () => {
          if (el.style.maxHeight === '0px') el.style.display = 'none';
        };
        if (animate) el.addEventListener('transitionend', finish, { once: true });
        else finish();
      }

      if (!animate) {
        requestAnimationFrame(() => {
          el.style.transitionDuration = '0.3s';
        });
      }
    }

    persistExpanded(heading, isOpening) {
      const span = heading.querySelector('span');
      const headingText = span ? span.textContent.trim() : '';
      if (!headingText) return;

      let expanded = this.getExpandedGroups();
      if (isOpening) {
        if (expanded.indexOf(headingText) === -1) expanded.push(headingText);
      } else {
        expanded = expanded.filter((text) => text !== headingText);
      }
      localStorage.setItem(this._expandedStorageKey, JSON.stringify(expanded));
    }

    getExpandedGroups() {
      try {
        return JSON.parse(localStorage.getItem(this._expandedStorageKey) || '[]');
      } catch (error) {
        return [];
      }
    }

    initAccordionState() {
      if (!this.sidebar) return;
      const expanded = this.getExpandedGroups();

      this.sidebar.querySelectorAll('[data-action="toggle-accordion"]').forEach((heading) => {
        const group = heading.closest('.collection__sidebar__group');
        const hasActive = group ? !!group.querySelector('.sidebar__item--active') : false;
        const span = heading.querySelector('span');
        const headingText = span ? span.textContent.trim() : '';
        const isPersisted = headingText && expanded.indexOf(headingText) !== -1;
        const shouldOpen = heading.classList.contains('is-active') || hasActive || isPersisted;

        this.setAccordionGroup(heading, shouldOpen, false);

        if (!group) return;
        const hiddenItems = group.querySelectorAll('.sidebar__item.is-hidden');
        let hasHiddenSelected = false;
        hiddenItems.forEach((item) => {
          const input = item.querySelector('input[type="checkbox"]');
          if (input && input.checked) hasHiddenSelected = true;
        });
        if (hasHiddenSelected) {
          hiddenItems.forEach((item) => {
            item.classList.remove('is-hidden');
            item.removeAttribute('data-link-hidden');
          });
          const moreOptions = group.querySelector('.collection__sidebar__actions');
          if (moreOptions) moreOptions.style.display = 'none';
        }
      });
    }

    initRangeSliders() {
      this.querySelectorAll('[data-range-slider]').forEach((slider) => this.renderRangeSlider(slider));
    }

    numericAttribute(el, attr, fallback) {
      const value = parseFloat(el.getAttribute(attr));
      return Number.isNaN(value) ? fallback : value;
    }

    positionRangeHandles(slider, { min, max, minValue, maxValue }) {
      const leftHandle = slider.querySelector('[data-range-left]');
      const rightHandle = slider.querySelector('[data-range-right]');
      const line = slider.querySelector('[data-range-line]');
      if (!leftHandle || !rightHandle) return;

      const handleWidth = leftHandle.offsetWidth || 16;
      const usable = Math.max(1, slider.offsetWidth - handleWidth);
      const leftPx = ((minValue - min) / (max - min)) * usable;
      const rightPx = ((maxValue - min) / (max - min)) * usable;

      leftHandle.style.left = `${leftPx}px`;
      rightHandle.style.left = `${rightPx}px`;
      if (line) {
        line.style.marginLeft = `${leftPx + handleWidth / 2}px`;
        line.style.width = `${Math.max(0, rightPx - leftPx)}px`;
      }
    }

    renderRangeSlider(slider) {
      const min = parseFloat(slider.getAttribute('data-se-min')) || 0;
      const max = parseFloat(slider.getAttribute('data-se-max')) || 100;
      const minValue = this.numericAttribute(slider, 'data-se-min-value', min);
      const maxValue = this.numericAttribute(slider, 'data-se-max-value', max);
      this.positionRangeHandles(slider, { min, max, minValue, maxValue });
    }

    onHandleDown(event) {
      const leftHandle = event.target.closest('[data-range-left]');
      const rightHandle = event.target.closest('[data-range-right]');
      const handle = leftHandle || rightHandle;
      if (!handle) return;

      const slider = handle.closest('[data-range-slider]');
      if (!slider) return;

      event.preventDefault();
      this.startRangeDrag(slider, leftHandle ? 'left' : 'right');
    }

    startRangeDrag(slider, side) {
      const leftHandle = slider.querySelector('[data-range-left]');
      const holder = slider.closest('[data-range-holder]');
      const priceMinInput = holder ? holder.querySelector('[data-field-price-min]') : null;
      const priceMaxInput = holder ? holder.querySelector('[data-field-price-max]') : null;

      const min = parseFloat(slider.getAttribute('data-se-min')) || 0;
      const max = parseFloat(slider.getAttribute('data-se-max')) || 100;
      const step = parseFloat(slider.getAttribute('data-se-step')) || 1;
      let minValue = this.numericAttribute(slider, 'data-se-min-value', min);
      let maxValue = this.numericAttribute(slider, 'data-se-max-value', max);

      const handleWidth = leftHandle.offsetWidth || 16;

      const render = () => {
        this.positionRangeHandles(slider, { min, max, minValue, maxValue });
        if (priceMinInput) priceMinInput.value = Math.round(minValue);
        if (priceMaxInput) priceMaxInput.value = Math.round(maxValue);
      };

      const move = (event) => {
        const pageX = event.touches ? event.touches[0].pageX : event.pageX;
        const rect = slider.getBoundingClientRect();
        const usable = Math.max(1, rect.width - handleWidth);
        let ratio = (pageX - rect.left - handleWidth / 2) / usable;
        ratio = Math.max(0, Math.min(1, ratio));
        let value = min + ratio * (max - min);
        value = Math.round(value / step) * step;

        if (side === 'left') minValue = Math.max(min, Math.min(value, maxValue));
        else maxValue = Math.min(max, Math.max(value, minValue));

        render();
      };

      const stopDrag = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', stopDrag);
        document.removeEventListener('touchmove', move);
        document.removeEventListener('touchend', stopDrag);

        slider.setAttribute('data-se-min-value', Math.round(minValue));
        slider.setAttribute('data-se-max-value', Math.round(maxValue));
      };

      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', stopDrag);
      document.addEventListener('touchmove', move, { passive: false });
      document.addEventListener('touchend', stopDrag);

      render();
    }

    escape(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    handleize(str) {
      return String(str)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      if (this.gridWrapper) this.gridWrapper.classList.toggle('is-loading', isLoading);
      if (this.productGrid) this.productGrid.setAttribute('aria-busy', isLoading);
    }
  }

  customElements.define('product-list', ProductList);
}
